/**
 * Potential-Analyzer-Profile ("ICP-Sets").
 *
 * Spiegelt das Multi-Config-Muster des List Cleaners
 * (`settings/cleanupGuidelinesList`): eine Map benannter Profile unter
 * `settings/potentialAnalyzerList`, von denen genau eines `isDefault` trägt.
 *
 * Die alte Einzel-Config unter `settings/potentialAnalyzerSettings` bleibt
 * bestehen: ihr `enabled` ist weiterhin der Hauptschalter des Features, und
 * ihre ICP-Felder dienen als letzter Fallback, solange kein Profil existiert.
 *
 * Bewusst frei von Firebase-Imports, damit die Auflösungs-Logik testbar bleibt
 * und Frontend wie Settings-UI dieselben Regeln nutzen.
 */

export const potentialProfilesPath = (userId) => `/World/${userId}/settings/potentialAnalyzerList`;
export const potentialLegacyPath = (userId) => `/World/${userId}/settings/potentialAnalyzerSettings`;

/** ICP-Felder eines Profils inkl. Defaults. `name`/`id` kommen separat dazu. */
export const POTENTIAL_PROFILE_FIELDS = {
  description: '',
  revenueSize: '',
  employeeMin: '',
  employeeMax: '',
  strictness: 'balanced', // 'lenient' | 'balanced' | 'strict'
  language: 'auto',
};

export const STRICTNESS_OPTIONS = [
  { id: 'lenient', label: 'Locker', desc: 'Im Zweifel höher bewerten', icon: 'fa-feather' },
  { id: 'balanced', label: 'Ausgewogen', desc: 'Fair & realistisch', icon: 'fa-scale-balanced' },
  { id: 'strict', label: 'Streng', desc: 'Nur klare Treffer top', icon: 'fa-gavel' },
];

export const LANGUAGE_OPTIONS = [
  { id: 'auto', label: '🌍 Auto' },
  { id: 'German', label: '🇩🇪 Deutsch' },
  { id: 'English', label: '🇬🇧 English' },
];

/** Feste ID des aus der Alt-Config übernommenen Profils — verhindert Duplikate. */
export const LEGACY_PROFILE_ID = 'legacy';

export const makePotentialProfile = (name, base = {}) => ({
  ...POTENTIAL_PROFILE_FIELDS,
  id: `${Date.now()}`,
  name: (name || '').trim() || 'Neues Profil',
  isDefault: false,
  createdAt: Date.now(),
  ...base,
});

/** RTDB-Map → Array, neueste zuerst (wie beim Cleaner). */
export const profilesFromSnapshot = (value) =>
  Object.entries(value || {})
    .filter(([, profile]) => profile && typeof profile === 'object')
    .map(([id, profile]) => ({ ...POTENTIAL_PROFILE_FIELDS, ...profile, id }))
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));

export const pickDefaultProfile = (profiles) => {
  const list = Array.isArray(profiles) ? profiles : [];
  return list.find(p => p.isDefault) || list[0] || null;
};

/** True, sobald ein Profil der KI überhaupt etwas zu bewerten gibt. */
export const hasIcpContent = (profile) =>
  !!(profile && (
    (profile.description || '').trim() ||
    (profile.revenueSize || '').trim() ||
    profile.employeeMin ||
    profile.employeeMax
  ));

/** Nur ICP-Felder — nicht `enabled`/`mode`/`updatedAt` aus der Alt-Config. */
export const icpFieldsFrom = (src = {}) => {
  const out = {};
  Object.keys(POTENTIAL_PROFILE_FIELDS).forEach((key) => {
    if (src[key] !== undefined && src[key] !== null) out[key] = src[key];
  });
  return out;
};

/** Alte Einzel-Config als Profil, damit Einzelläufe ohne Profil-Liste weiterlaufen. */
export const legacySettingsAsProfile = (legacySettings) => ({
  ...POTENTIAL_PROFILE_FIELDS,
  ...icpFieldsFrom(legacySettings || {}),
  id: null,
  name: 'Standard-ICP',
});

/**
 * Wählt das Profil für einen Lauf:
 *   1. explizite ID (Agent / Bulk-Picker) — auch wenn das Profil leer ist
 *   2. Default-/erstes Profil, sofern es ICP-Inhalt hat
 *   3. Alt-Config `potentialAnalyzerSettings` (einzelne Legacy-Research)
 *   4. sonst das leere Default-Profil bzw. leere Alt-Felder
 *
 * Ein leeres neues Profil darf eine befüllte Alt-Config nicht verdecken —
 * sonst sterben Einzelläufe über das Research-Menü nach dem Multi-Umzug.
 */
export const resolvePotentialProfile = (profiles, legacySettings, profileId) => {
  const list = Array.isArray(profiles) ? profiles : [];
  if (profileId) {
    const hit = list.find(p => p.id === profileId);
    if (hit) return hit;
  }
  const picked = pickDefaultProfile(list);
  if (picked && hasIcpContent(picked)) return picked;
  if (hasIcpContent(legacySettings)) return legacySettingsAsProfile(legacySettings);
  return picked || legacySettingsAsProfile(legacySettings);
};

/** Payload-Teil für `analyzePotential` — identisch für alle Aufrufwege. */
export const buildPotentialIcp = (profile) => {
  const p = profile || POTENTIAL_PROFILE_FIELDS;
  return {
    description: p.description || '',
    revenueSize: p.revenueSize || '',
    employeeMin: p.employeeMin === '' || p.employeeMin === undefined ? null : p.employeeMin,
    employeeMax: p.employeeMax === '' || p.employeeMax === undefined ? null : p.employeeMax,
    strictness: p.strictness || 'balanced',
  };
};

/**
 * Wie gut ist ein Profil ausgefüllt (0–10)? Bewertet Vollständigkeit, nicht
 * Qualität — analog zum Setup-Score der Cleaner-Sets.
 */
export const potentialProfileScore = (profile) => {
  let score = 0;
  if ((profile?.name || '').trim()) score += 1;

  const desc = (profile?.description || '').trim();
  if (desc.length >= 240) score += 5;
  else if (desc.length >= 80) score += 4;
  else if (desc.length >= 1) score += 2;

  if ((profile?.revenueSize || '').trim()) score += 2;
  if (profile?.employeeMin || profile?.employeeMax) score += 2;

  return Math.max(0, Math.min(10, score));
};

export const potentialProfileScoreTone = (score) => {
  if (score >= 8) return 'strong';
  if (score >= 5) return 'medium';
  if (score >= 1) return 'starter';
  return 'empty';
};

export const potentialProfileScoreLabel = (score) => {
  if (score >= 10) return 'Vollständig';
  if (score >= 8) return 'Gut befüllt';
  if (score >= 5) return 'Brauchbar';
  if (score >= 1) return 'Angefangen';
  return 'Leer';
};
